import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { fetchLegislation, fetchCountries, CountryCount, Legislation } from '../../../services/api'
import { useTheme } from '../../layout/ThemeProvider'
import { countryMeta } from '../Analysis/helpers'

const PAGE_SIZE = 50

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const TYPE_COLOURS: Record<string, string> = {
  Regulation: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  Directive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  Decision: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  Recommendation: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  Opinion: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
}

const Home: React.FC = () => {
  const { theme, toggleTheme } = useTheme()
  const [items, setItems] = useState<Legislation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [countries, setCountries] = useState<CountryCount[]>([])
  const [country, setCountry] = useState<string | undefined>(undefined)

  useEffect(() => {
    fetchCountries()
      .then(setCountries)
      .catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchLegislation(PAGE_SIZE, page * PAGE_SIZE, country)
      .then(data => {
        setItems(data)
        setHasMore(data.length === PAGE_SIZE)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [page, country])

  const selectCountry = (next: string | undefined) => {
    setCountry(next)
    setPage(0)
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-6 pt-5 pb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-[18px] h-[18px] rounded-[5px] bg-an-accent text-white text-[8.5px] font-extrabold leading-none">LT</span>
            <span className="text-[12px] font-semibold text-an-ink tracking-[-0.02em]">Legislation Tracker</span>
          </div>
          <div className="flex items-center gap-2.5 text-[11.5px]">
            <Link to="/" className="text-an-ink-3 font-medium no-underline hover:text-an-ink-2 transition-colors">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="inline mr-0.5 -mt-px">
                <path d="M7 2l-4 3 4 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Analysis
            </Link>
            <div className="w-px h-3 bg-an-line-mid" />
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-6 h-6 rounded text-an-ink-3 hover:text-an-ink-2 hover:bg-accent transition-colors cursor-pointer bg-transparent border-none"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.76 2.76l1.06 1.06M10.18 10.18l1.06 1.06M11.24 2.76l-1.06 1.06M3.82 10.18l-1.06 1.06" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M12.5 8.5a5.5 5.5 0 01-7-7A5.5 5.5 0 1012.5 8.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {countries.length > 1 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <button
              onClick={() => selectCountry(undefined)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${country === undefined ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
            >
              All
            </button>
            {countries.map(c => (
              <button
                key={c.country}
                onClick={() => selectCountry(c.country)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${country === c.country ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
              >
                {countryMeta(c.country).flag} {countryMeta(c.country).label} <span className="opacity-60">({c.count})</span>
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Title</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Source</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Reference</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }, (_, i) => (
                  <tr key={i} className={`border-b border-border last:border-0 ${i % 2 !== 0 ? 'bg-muted/10' : ''}`}>
                    <td className="px-4 py-3"><div className="h-4 w-3/4 animate-pulse rounded bg-muted" /><div className="mt-1.5 h-3 w-1/2 animate-pulse rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-10 animate-pulse rounded-full bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-5 w-16 animate-pulse rounded-full bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-20 animate-pulse rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-16 animate-pulse rounded bg-muted" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">Failed to load legislation: {error}</p>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-muted-foreground">No legislation found.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Title</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Source</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr
                      key={item.id}
                      className={`border-b border-border last:border-0 transition-colors hover:bg-muted/30 ${i % 2 === 0 ? '' : 'bg-muted/10'}`}
                    >
                      <td className="px-4 py-3">
                        <span className="line-clamp-2 text-foreground">{item.title || '—'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {countryMeta(item.country).flag} {countryMeta(item.country).label}
                        </span>
                        {item.source_name && (
                          <span className="ml-2 text-xs text-muted-foreground">{item.source_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.document_type ? (
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLOURS[item.document_type] ?? 'bg-muted text-muted-foreground'}`}>
                            {item.document_type_raw || item.document_type}
                          </span>
                        ) : item.document_type_raw ? (
                          <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            {item.document_type_raw}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {formatDate(item.document_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.source_url ? (
                          <a
                            href={item.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {item.celex_number || 'View'}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{item.celex_number || '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-muted-foreground">Page {page + 1}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Home
